import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { I18n } from 'react-redux-i18n';
import { formatLocalIsoDate, maskDobInput } from './dobDate';
import { getErrorCopyKey, PERSON_ERROR_CODES } from './errorCopy';
import { formatResetTime, getResetAt } from './quotaReset';
import {
  getRelatedTopics,
  preparePersonContext,
  sendChatMessage,
} from './nawiChatApi';

const resizeTextarea = (textarea) => {
  textarea.style.height = 'auto';
  textarea.style.height = `${Math.min(textarea.scrollHeight, 180)}px`;
};

const isAbortError = (error) => error?.name === 'AbortError';

const QUOTA_SCOPES = ['user', 'total'];

const getUiLanguage = () => {
  try {
    return window.localStorage.getItem('language') || 'es';
  } catch (_error) {
    return 'es';
  }
};

// Builds the fixed, user-facing copy for a 429 quota error. The reset time is
// local to the user's browser; the composer stays locked until `resetAt`.
const buildQuotaNotice = (error, now = Date.now()) => {
  const resetAt = getResetAt(error?.retryAfterSeconds, now);
  const scope = QUOTA_SCOPES.includes(error?.scope) ? error.scope : 'hourly';
  const time = resetAt
    ? formatResetTime(resetAt, now, getUiLanguage())
    : I18n.t('persons.nawiChat.quota.timeUnknown');

  return {
    resetAt,
    text: I18n.t(`persons.nawiChat.quota.${scope}`, { time }),
  };
};

const NOT_ENABLED_ERROR_CODES = [
  'user_not_provisioned',
  'tenant_not_enabled',
  'account_deactivated',
  'api_access_not_enabled',
  'email_not_allowed',
];

const useNawiChat = ({ accountId, userPlayerId } = {}) => {
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([]);
  const [isResponding, setIsResponding] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [copyStatus, setCopyStatus] = useState({});
  const [contextStatus, setContextStatus] = useState('loading');
  const [contextError, setContextError] = useState(false);
  const [accessDeniedCode, setAccessDeniedCode] = useState(null);
  const [personErrorCode, setPersonErrorCode] = useState(null);
  const [dobValue, setDobValue] = useState(null);
  const [quotaResetAt, setQuotaResetAt] = useState(null);
  const activeResponseController = useRef(null);
  const controllers = useRef(new Set());
  const copyTimer = useRef(null);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messageId = useRef(0);
  const clientMessageSequence = useRef(0);
  const chatId = useRef(null);
  const inputValueRef = useRef('');
  const isRespondingRef = useRef(false);
  const mounted = useRef(true);
  const quotaResetAtRef = useRef(null);

  const registerController = useCallback(() => {
    const controller = new AbortController();
    controllers.current.add(controller);
    return controller;
  }, []);

  const releaseController = useCallback((controller) => {
    controllers.current.delete(controller);
  }, []);

  const initializeContext = useCallback(
    async (dob) => {
      if (!Number.isInteger(userPlayerId) || userPlayerId <= 0) {
        setContextStatus('error');
        return;
      }

      const controller = registerController();
      setContextError(false);
      setAccessDeniedCode(null);
      setPersonErrorCode(null);
      setContextStatus('loading');

      try {
        await preparePersonContext({
          accountId,
          dob,
          signal: controller.signal,
          userPlayerId,
        });
        if (mounted.current) setContextStatus('ready');
      } catch (error) {
        if (!mounted.current || isAbortError(error)) return;
        if (error?.code === 'dob_required' || error?.code === 'invalid_dob') {
          setContextError(error.code === 'invalid_dob');
          setContextStatus('dob_required');
        } else if (NOT_ENABLED_ERROR_CODES.includes(error?.code)) {
          setAccessDeniedCode(error.code);
          setContextStatus('not_enabled');
        } else if (PERSON_ERROR_CODES.includes(error?.code)) {
          setPersonErrorCode(error.code);
          setContextStatus('person_unavailable');
        } else {
          setContextStatus('error');
        }
      } finally {
        releaseController(controller);
      }
    },
    [accountId, registerController, releaseController, userPlayerId],
  );

  useEffect(() => {
    mounted.current = true;
    controllers.current.forEach((controller) => controller.abort());
    controllers.current.clear();
    activeResponseController.current = null;
    chatId.current = null;
    messageId.current = 0;
    clientMessageSequence.current = 0;
    inputValueRef.current = '';
    isRespondingRef.current = false;
    quotaResetAtRef.current = null;
    setQuotaResetAt(null);
    setInputValue('');
    setMessages([]);
    setIsResponding(false);
    setShowScrollButton(false);
    setCopyStatus({});
    setDobValue(null);
    initializeContext();

    return () => {
      mounted.current = false;
      controllers.current.forEach((controller) => controller.abort());
      controllers.current.clear();
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
    };
  }, [initializeContext]);

  // Re-enables the composer once the quota resets. The timer never sends or
  // retries anything; it only clears the lock and is cleaned up on unmount.
  useEffect(() => {
    if (!quotaResetAt) return undefined;

    const timer = window.setTimeout(() => {
      quotaResetAtRef.current = null;
      setQuotaResetAt(null);
    }, Math.max(quotaResetAt - Date.now(), 0));

    return () => window.clearTimeout(timer);
  }, [quotaResetAt]);

  useEffect(() => {
    if (
      !showScrollButton &&
      typeof messagesEndRef.current?.scrollIntoView === 'function'
    ) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isResponding, showScrollButton]);

  useEffect(() => {
    if (textareaRef.current) resizeTextarea(textareaRef.current);
  }, [inputValue]);

  const updateAssistantMessage = useCallback((id, updater) => {
    if (!mounted.current) return;
    setMessages((currentMessages) =>
      currentMessages.map((message) =>
        message.id === id ? updater(message) : message,
      ),
    );
  }, []);

  const nextClientMessageId = useCallback(() => {
    clientMessageSequence.current += 1;
    return `nawi:${Date.now().toString(36)}:${clientMessageSequence.current}`;
  }, []);

  const sendMessage = useCallback(
    async (messageValue) => {
      const content = messageValue.trim();
      if (
        !content ||
        isRespondingRef.current ||
        quotaResetAtRef.current ||
        contextStatus !== 'ready'
      ) {
        return;
      }

      messageId.current += 1;
      const userMessageId = messageId.current;
      messageId.current += 1;
      const assistantMessageId = messageId.current;
      let streamedText = '';

      setMessages((currentMessages) => [
        ...currentMessages,
        { id: userMessageId, sender: 'user', text: content },
        {
          id: assistantMessageId,
          sender: 'assistant',
          status: 'streaming',
          text: '',
          topics: [],
        },
      ]);
      inputValueRef.current = '';
      setInputValue('');
      isRespondingRef.current = true;
      setIsResponding(true);

      const controller = registerController();
      activeResponseController.current = controller;

      try {
        const result = await sendChatMessage({
          accountId,
          chatId: chatId.current || undefined,
          clientMessageId: nextClientMessageId(),
          content,
          onChunk: (chunk) => {
            streamedText += chunk;
            updateAssistantMessage(assistantMessageId, (message) => ({
              ...message,
              text: `${message.text}${chunk}`,
            }));
          },
          onMeta: (meta) => {
            if (chatId.current && chatId.current !== meta.chatId) {
              throw new Error('Active NAWI chat changed unexpectedly');
            }
            chatId.current = meta.chatId;
          },
          signal: controller.signal,
          userPlayerId,
        });

        if (!streamedText || !result.chatId) {
          throw new Error('NAWI returned an incomplete response');
        }

        chatId.current = result.chatId;
        updateAssistantMessage(assistantMessageId, (message) => ({
          ...message,
          status: 'complete',
        }));

        if (activeResponseController.current === controller) {
          activeResponseController.current = null;
          isRespondingRef.current = false;
          if (mounted.current) setIsResponding(false);
        }

        try {
          const topics = await getRelatedTopics({
            accountId,
            chatId: result.chatId,
            signal: controller.signal,
            userPlayerId,
          });
          updateAssistantMessage(assistantMessageId, (message) => ({
            ...message,
            topics,
          }));
        } catch (error) {
          if (!isAbortError(error)) {
            // Related topics are optional and must not invalidate a complete answer.
          }
        }
      } catch (error) {
        if (isAbortError(error)) {
          if (streamedText) {
            updateAssistantMessage(assistantMessageId, (message) => ({
              ...message,
              status: 'stopped',
            }));
          } else if (mounted.current) {
            setMessages((currentMessages) =>
              currentMessages.filter(
                (message) => message.id !== assistantMessageId,
              ),
            );
          }
        } else if (error?.code === 'nawi_quota_exceeded') {
          // Never retried automatically: the quota resets on the backend clock.
          const notice = buildQuotaNotice(error);
          if (notice.resetAt && mounted.current) {
            quotaResetAtRef.current = notice.resetAt;
            setQuotaResetAt(notice.resetAt);
          }
          updateAssistantMessage(assistantMessageId, (message) => ({
            ...message,
            status: 'error',
            text: message.text || notice.text,
          }));
        } else if (getErrorCopyKey(error?.code)) {
          // Fixed copy for wallet and person errors; never retried.
          updateAssistantMessage(assistantMessageId, (message) => ({
            ...message,
            status: 'error',
            text: message.text || I18n.t(getErrorCopyKey(error.code)),
          }));
        } else if (error?.code === 'rate_limited') {
          updateAssistantMessage(assistantMessageId, (message) => ({
            ...message,
            status: 'error',
            text: message.text || I18n.t('persons.nawiChat.rateLimited'),
          }));
        } else if (error?.code === 'stream_error' && streamedText) {
          // The provider saved the partial answer: keep it and tell the user
          // not to resend the question.
          updateAssistantMessage(assistantMessageId, (message) => ({
            ...message,
            status: 'incomplete',
          }));
        } else {
          const safeText = error?.safeMessage || I18n.t('chatbot.error');
          updateAssistantMessage(assistantMessageId, (message) => ({
            ...message,
            status: 'error',
            text: message.text || safeText,
          }));
        }
      } finally {
        releaseController(controller);
        if (activeResponseController.current === controller) {
          activeResponseController.current = null;
          isRespondingRef.current = false;
          if (mounted.current) setIsResponding(false);
        }
      }
    },
    [
      accountId,
      contextStatus,
      nextClientMessageId,
      registerController,
      releaseController,
      updateAssistantMessage,
      userPlayerId,
    ],
  );

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      sendMessage(inputValueRef.current);
    },
    [sendMessage],
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && !event.shiftKey) handleSubmit(event);
    },
    [handleSubmit],
  );

  const handleStop = useCallback(() => {
    activeResponseController.current?.abort();
  }, []);

  const handleWorkspaceScroll = useCallback((event) => {
    const workspace = event.currentTarget;
    const distanceFromBottom =
      workspace.scrollHeight - workspace.scrollTop - workspace.clientHeight;
    setShowScrollButton(distanceFromBottom > 120);
  }, []);

  const handleScrollDown = useCallback(() => {
    if (typeof messagesEndRef.current?.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    setShowScrollButton(false);
  }, []);

  const handleCopy = useCallback((message) => {
    if (!navigator.clipboard?.writeText) {
      setCopyStatus({ id: message.id, value: 'error' });
      return;
    }

    navigator.clipboard
      .writeText(message.text)
      .then(() => {
        if (copyTimer.current) window.clearTimeout(copyTimer.current);
        setCopyStatus({ id: message.id, value: 'copied' });
        copyTimer.current = window.setTimeout(() => {
          setCopyStatus({});
          copyTimer.current = null;
        }, 2000);
      })
      .catch(() => {
        setCopyStatus({ id: message.id, value: 'error' });
      });
  }, []);

  const handleInputChange = useCallback((event) => {
    inputValueRef.current = event.target.value;
    setInputValue(event.target.value);
  }, []);

  const handleDobChange = useCallback((date) => {
    setDobValue(date);
    setContextError(false);
  }, []);

  // Fires before onChange on every keystroke: typed text that does not parse to
  // an allowed date must not keep submitting the previously valid one.
  const handleDobRawChange = useCallback((event) => {
    if (event?.target?.tagName !== 'INPUT') return;
    // The picker parses event.target.value right after this callback, so the
    // masked text is what it reads and shows.
    const deleting = Boolean(
      event.nativeEvent?.inputType?.startsWith('delete'),
    );
    event.target.value = maskDobInput(event.target.value, { deleting });
    setDobValue(null);
  }, []);

  const handleDobSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (dobValue) initializeContext(formatLocalIsoDate(dobValue));
    },
    [dobValue, initializeContext],
  );

  const actions = useMemo(
    () => ({
      handleCopy,
      handleDobChange,
      handleDobRawChange,
      handleDobSubmit,
      handleInputChange,
      handleKeyDown,
      handleScrollDown,
      handleStop,
      handleSubmit,
      handleWorkspaceScroll,
      sendMessage,
    }),
    [
      handleCopy,
      handleDobChange,
      handleDobRawChange,
      handleDobSubmit,
      handleInputChange,
      handleKeyDown,
      handleScrollDown,
      handleStop,
      handleSubmit,
      handleWorkspaceScroll,
      sendMessage,
    ],
  );

  const composer = useMemo(
    () => ({
      accessDeniedCode,
      contextError,
      contextStatus,
      dobValue,
      inputValue,
      isQuotaBlocked: Boolean(quotaResetAt),
      isResponding,
      personErrorCode,
      showScrollButton,
      textareaRef,
    }),
    [
      accessDeniedCode,
      contextError,
      contextStatus,
      dobValue,
      inputValue,
      isResponding,
      personErrorCode,
      quotaResetAt,
      showScrollButton,
    ],
  );

  const conversation = useMemo(
    () => ({
      copyStatus,
      isResponding,
      messages,
      messagesEndRef,
    }),
    [copyStatus, isResponding, messages],
  );

  return { actions, composer, conversation };
};

export default useNawiChat;
